/**
 * formatApiError — coage `error.response.data.detail` retornado pela API
 * em string segura para renderizar em JSX ou passar para notifications.show().
 *
 * Motivacao: FastAPI retorna `detail` em 2 formatos:
 *   - HTTPException(detail="msg")        → string
 *   - 422 Pydantic ValidationError       → Array<{type, loc, msg, input}>
 *
 * Renderizar o array direto em JSX dispara React error #31
 * ("Objects are not valid as a React child"). Este helper cobre os dois
 * casos e tambem o fallback para AxiosError.message quando nao ha detail.
 */
export function formatApiError(err: unknown, fallback = 'Erro desconhecido'): string {
  const e = err as
    | { response?: { data?: { detail?: unknown } }; message?: string }
    | null
    | undefined
  const detail = e?.response?.data?.detail

  if (typeof detail === 'string') return detail

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => formatPydanticItem(item))
      .filter((s): s is string => !!s)
    if (parts.length > 0) return parts.join('; ')
  }

  if (detail != null && typeof detail === 'object') {
    // detail estruturado do backend: HTTPException(detail={'code', 'message'}).
    // A mensagem ja vem pronta em PT-BR — mostrar ela, nao o JSON.
    const structured = (detail as { message?: unknown }).message
    if (typeof structured === 'string' && structured) return structured

    // `{classification, error}` — shape que `_require_tenant_session` devolve
    // no 503 do ZTNA. Sem este ramo o helper caía no JSON.stringify abaixo e
    // punha o objeto cru na tela; a causa real (AccessDenied, expired token)
    // mora em `error`.
    const causa = (detail as { error?: unknown }).error
    if (typeof causa === 'string' && causa) return causa

    const item = formatPydanticItem(detail)
    if (item) return item
    try {
      return JSON.stringify(detail)
    } catch {
      // ignore — cai no fallback abaixo
    }
  }

  return e?.message ?? fallback
}

function formatPydanticItem(item: unknown): string | null {
  if (typeof item === 'string') return item
  if (item == null || typeof item !== 'object') return null
  const obj = item as { msg?: unknown; loc?: unknown }
  const msg = typeof obj.msg === 'string' ? obj.msg : null
  if (!msg) return null
  if (Array.isArray(obj.loc) && obj.loc.length > 1) {
    const field = obj.loc[obj.loc.length - 1]
    if (typeof field === 'string' || typeof field === 'number') {
      return `${field}: ${msg}`
    }
  }
  return msg
}
